/**
 * Logique pure de classement des utilisateurs par plan pour le dashboard admin.
 *
 * Volontairement isole de `adminUsersByPlan.ts` : ce fichier n'importe RIEN de
 * `wasp/server`, dont le simple import valide les variables d'environnement et
 * fait echouer les tests unitaires au chargement du module.
 */
import { PaymentPlanId, isGracePeriodExpired } from "../payment/plans";

/** Colonnes affichees : les variantes annuelles rejoignent leur tier mensuel. */
export type AdminPlanColumnKey = "max" | "pro" | "free";

/**
 * Revenu mensuel reconnu par plan, en euros.
 *
 * Les prix font autorite chez Stripe, pas ici : cette table sert uniquement a
 * afficher un MRR indicatif dans l'admin. Les plans annuels sont ramenes a leur
 * equivalent mensuel (144/12 et 288/12) pour que le total reste homogene, sinon
 * un abonne annuel gonflerait le MRR de 12 mois d'un coup.
 * A mettre a jour si la grille tarifaire bouge.
 */
export const MONTHLY_REVENUE_BY_PLAN: Record<string, number> = {
  [PaymentPlanId.Pro]: 15,
  [PaymentPlanId.ProAnnual]: 144 / 12,
  [PaymentPlanId.Max]: 30,
  [PaymentPlanId.MaxAnnual]: 288 / 12,
};

export const ANNUAL_PLANS: string[] = [
  PaymentPlanId.ProAnnual,
  PaymentPlanId.MaxAnnual,
];

/** Au-dela de ce delai sans activite, la ligne est signalee comme inactive. */
export const INACTIVE_AFTER_DAYS = 30;

/**
 * Plan effectif d'un utilisateur pour l'affichage admin.
 *
 * Reproduit la regle metier de la periode de grace : un abonne dont le
 * prelevement a echoue depuis plus de 7 jours est traite comme Free partout
 * dans l'app, il doit donc apparaitre dans la colonne Free ici aussi. Classer
 * sur `subscriptionPlan` brut afficherait un payant fantome.
 */
export function resolveColumn(
  subscriptionPlan: string | null,
  paymentFailedAt: Date | null,
): AdminPlanColumnKey {
  if (isGracePeriodExpired(paymentFailedAt)) {
    return "free";
  }
  if (
    subscriptionPlan === PaymentPlanId.Max ||
    subscriptionPlan === PaymentPlanId.MaxAnnual
  ) {
    return "max";
  }
  if (
    subscriptionPlan === PaymentPlanId.Pro ||
    subscriptionPlan === PaymentPlanId.ProAnnual
  ) {
    return "pro";
  }
  return "free";
}

/** Fenetre glissante de l'indicateur « actifs » affiche en tete de colonne. */
export const ACTIVE_WITHIN_DAYS = 7;

/**
 * Un utilisateur est « actif » s'il a ouvert l'app dans la fenetre donnee.
 *
 * S'appuie sur `lastActiveAt`, ecrit par le ping de presence (POST
 * /api/ping-activity, throttle a 5 min) : c'est donc bien une connexion reelle,
 * pas une simple existence de compte. Un compte sans `lastActiveAt` n'a jamais
 * ouvert l'app, il n'est jamais actif.
 */
export function isActiveWithin(
  lastActiveAt: Date | null,
  now: Date,
  days: number = ACTIVE_WITHIN_DAYS,
): boolean {
  if (!lastActiveAt) return false;
  const elapsed = now.getTime() - new Date(lastActiveAt).getTime();
  // Une date future (horloge decalee) reste comptee comme active : elapsed
  // negatif est bien <= la fenetre.
  return elapsed <= days * 24 * 60 * 60 * 1000;
}
